import { useEffect, useMemo, useState } from "react";
import { Button, DropdownSelect, Icon, Loading, Modal, Panel, Tooltip, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator } from "@/hooks";
import { useGetVehicleFormOptionsQuery, vehicleApi } from "@/redux/apis/vehicle/vehicleApi";
import { useUpdateAffiliateVehicleMutation } from "@/redux/apis/vehicleGuest/vehicleGuestApi";
import { useAppDispatch } from "@/redux/hooks";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { addToast, getAssetUrl } from "@/utils";

interface SelectVehicleTypeModalProps {
  vehicle: Vehicle | undefined;
  onClose: () => void;
  approveOnSave?: boolean;
}

export const SelectVehicleTypeModal = ({ vehicle, onClose, approveOnSave = true }: SelectVehicleTypeModalProps) => {
  const { id: operatorId } = useOperator();
  const dispatch = useAppDispatch();
  const [selectedTypeUuid, setSelectedTypeUuid] = useState(vehicle?.vehicleType?.uuid ?? "");

  useEffect(() => {
    setSelectedTypeUuid(vehicle?.vehicleType?.uuid ?? "");
  }, [vehicle]);
  const { data: options, isFetching: isFetchingOptions } = useGetVehicleFormOptionsQuery();
  const [updateAffiliateVehicle, { isLoading }] = useUpdateAffiliateVehicleMutation();

  const vehicleTypes = useMemo(() => {
    if (!options?.vehicleTypes) return [];
    return options.vehicleTypes.map((vt) => ({ name: vt.name, value: vt.uuid }));
  }, [options?.vehicleTypes]);

  const handleSave = () => {
    if (!vehicle || !selectedTypeUuid) return;

    updateAffiliateVehicle({
      ownerId: operatorId,
      vehicleId: vehicle.uuid,
      ...(approveOnSave && { approval_status: "APPROVED" }),
      vehicle_type_uuid: selectedTypeUuid,
    })
      .unwrap()
      .then(() => {
        addToast("success", approveOnSave ? "Vehicle approved successfully" : "Vehicle type updated successfully");
        dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
        setSelectedTypeUuid("");
        onClose();
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  if (!vehicle) return null;

  return (
    <Modal open={!!vehicle} onClose={onClose} className="w-[85vw] !overflow-visible !p-6 md:w-[450px]">
      <div className="relative flex flex-col">
        {isLoading && <Loading className="rounded-lg" />}

        <Typography variant="title">Select Vehicle Type</Typography>
        <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
          Select a vehicle type for {vehicle.owner.name}&apos;s vehicle
        </Typography>

        <Panel className="mt-5 flex flex-col rounded-lg bg-neutral-surface-gray">
          <div className="flex items-center gap-3">
            <img
              src={getAssetUrl(vehicle.avatar, { width: 80, height: 80, background: "transparent" })}
              alt={`${vehicle.model.make} ${vehicle.model.model}`}
              className="h-9 w-9 shrink-0 rounded-full border border-neutral-mid-gray object-contain"
            />
            <div className="flex flex-col">
              <Typography variant="title">
                {vehicle.model.make} {vehicle.model.model}
              </Typography>
              <Typography variant="small" className="text-neutral-dark-gray">
                {vehicle.registrationNumber}
              </Typography>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-y-3 mt-6">
            <Typography className="text-neutral-dark-gray" variant="action">Year</Typography>
            <Typography className="text-right">{vehicle.year ?? "-"}</Typography>

            <Typography className="text-neutral-dark-gray" variant="action">Color</Typography>
            <div className="flex items-center justify-end gap-1.5 capitalize">
              <Typography>{vehicle.color ? vehicle.color.toLowerCase().replace("_", " ") : "-"}</Typography>
              {vehicle.color && (
                <span
                  className="inline-block h-3 w-3 rounded-full border border-neutral-mid-gray"
                  style={{ backgroundColor: vehicle.color.toLowerCase().replace("_", "") }}
                />
              )}
            </div>

            <Typography className="text-neutral-dark-gray" variant="action">Engine Type</Typography>
            <Typography className="text-right capitalize">{vehicle.model.engine.toLowerCase()}</Typography>

            <Typography className="text-neutral-dark-gray" variant="action">Passenger Capacity</Typography>
            <Typography className="text-right">{vehicle.capacity.pax} pax</Typography>

            <Typography className="text-neutral-dark-gray" variant="action">State Registered</Typography>
            <div className="flex items-center justify-end gap-1.5">
              {vehicle.state.image && <img src={vehicle.state.image} className="h-4 w-4 rounded-full object-cover" alt="" />}
              <Typography>{vehicle.state.abbreviation}</Typography>
            </div>

            <Typography className="text-neutral-dark-gray" variant="action">Registration Expiry</Typography>
            <Typography className="text-right">{vehicle.registration.expiryDate ?? "-"}</Typography>

            <Typography className="text-neutral-dark-gray" variant="action">VIN</Typography>
            <Typography className="text-right">{vehicle.vin ?? "-"}</Typography>

            <Typography className="text-neutral-dark-gray" variant="action">Vehicle Category</Typography>
            <Typography className="text-right">{vehicle.vehicleCategory?.name ?? "-"}</Typography>
          </div>
        </Panel>

        <div className="mt-5">
          <Typography variant="action" className="mb-1.5 flex items-center gap-1">
            Vehicle Type
            <Tooltip content="Operator-defined vehicle type used within your own fleet setup" placement="top">
              <Icon name="InfoCircle" size="sm" variant="Bold" className="rotate-180 text-neutral-silver-gray" />
            </Tooltip>
          </Typography>
          <DropdownSelect
            className="w-full"
            placeholder="Select Vehicle Type"
            options={vehicleTypes}
            value={selectedTypeUuid}
            onChange={(value) => setSelectedTypeUuid(value as string)}
            disabled={isFetchingOptions}
          />
        </div>

        <div className="mt-8 flex flex-col gap-3">
          <Button variant="primary" size="lg" className="w-full" onClick={handleSave} disabled={!selectedTypeUuid || isLoading}>
            {approveOnSave ? "Approve Vehicle" : "Save Vehicle Type"}
          </Button>
          <Button variant="secondary" size="lg" className="w-full" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};
